import Head from 'next/head';
import React, {useEffect} from 'react';
import orderStyles from 'styles/order.module.css';
import {useAppDispatch, useAppSelector} from '@crm/services/store';
import {
    cleaningActions,
    cleaningSelectors,
    CleaningStep,
    DEFAULT_FLOW,
    getCleaningSteps
} from '@crm/services/cleaning-slice';
import CleaningPrice from '@crm/components/cleaning/cleaning-price';
import CleaningOrderHandlerCard from '@crm/components/cleaning/cleaning-order-handler-card';
import {CLEANING_STEP_SUBMITTING_ORDER} from "@crm/models/steps/cleaning_step_submitting_order";
import {housekeeprApi} from "@crm/api/housekeepr-api";
import {authSelectors} from "@crm/services/auth-slice";

const Bestil = () => {
    const dispatch = useAppDispatch();

    const started = useAppSelector(cleaningSelectors.selectStarted);
    const currentStep = useAppSelector(cleaningSelectors.selectStep);
    const flow = useAppSelector(cleaningSelectors.selectFlow);
    const cleaningAuthentication = useAppSelector(cleaningSelectors.selectAuthenticated);
    const isAuthenticated = useAppSelector(authSelectors.selectAuthenticated);
    const completed = useAppSelector(cleaningSelectors.selectCompleted);

    const shownStep: CleaningStep = completed ? CLEANING_STEP_SUBMITTING_ORDER : currentStep;
    const steps = getCleaningSteps(flow ?? DEFAULT_FLOW);

    const {
        data: serviceTypesResult
    } = housekeeprApi.useGetServiceTypesQuery();

    useEffect(() => {
        const serviceType = serviceTypesResult?.find(serviceType => serviceType.type == 'cleaning');
        if (!started && serviceType) {
            dispatch(cleaningActions.start({serviceTypeId: serviceType.id}));
        }
    }, [dispatch, started, serviceTypesResult]);

    useEffect(() => {
        if (isAuthenticated != cleaningAuthentication) {
            dispatch(cleaningActions.setAuthenticated(isAuthenticated));
        }
    }, [dispatch, isAuthenticated, cleaningAuthentication]);


    const setPage = (page: CleaningStep) => {
        if (!completed && currentStep !== CLEANING_STEP_SUBMITTING_ORDER) {
            dispatch(cleaningActions.setPage(page));
        }
    };

    return <>
        <Head>
            <title>Rengøring - Housekeepr</title>
            <meta name="description" content="Housekeepr - Bestil service og beregn pris" className=""/>
            <meta name="robots" content="noindex"/>
            <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1"/>
            <link rel="icon" href="/favicon.ico"/>
            <script
                id="CookieConsent"
                src="https://policy.app.cookieinformation.com/uc.js"
                data-gcm-version="2.0"
                data-culture="DA"
                type="text/javascript"
                defer
            />
        </Head>
        <div className={orderStyles.container}>
            {completed
                ? <CleaningOrderHandlerCard
                    key={CLEANING_STEP_SUBMITTING_ORDER}
                />
                : <>
                    <div className={orderStyles.progressContainer}>
                        <div className={orderStyles.progressSteps}>
                            {started && steps.filter(step => !step.hideFromTrack()).map((step) => (
                                <div
                                    className={shownStep === step.getIdentifier() ? orderStyles.active : undefined}
                                    onClick={() => setPage(step.getIdentifier())}
                                    key={step.getIdentifier()}
                                >
                                    {step.getShortName() !== undefined
                                        ? <>
                                            <div className={orderStyles.desktopText}>
                                                {step.getName()}
                                            </div>
                                            <div className={orderStyles.mobileText}>
                                                {step.getShortName()}
                                            </div>
                                        </>
                                        : step.getName()
                                    }
                                </div>
                            ))}
                        </div>
                        <div className={orderStyles.rail}>
                            <div
                                className={orderStyles.progress}
                                style={{width: `min(100%, calc(${(steps.findIndex(value => value.getIdentifier() === shownStep) + 0.5) / steps.filter(value => !value.hideFromTrack()).length} * 100% + 4px))`}}
                            />
                        </div>
                    </div>
                    {steps.filter(step => !step.hideFromTrack()).map((step) => step.buildPage(shownStep))}
                    {shownStep == CLEANING_STEP_SUBMITTING_ORDER &&
                        steps.find(step => step.getIdentifier() === CLEANING_STEP_SUBMITTING_ORDER)?.buildPage(shownStep)
                    }
                </>
            }
        </div>
        {shownStep !== CLEANING_STEP_SUBMITTING_ORDER && <CleaningPrice/>}
    </>;
};

export default Bestil;
